/** Settle on cancellation even when the underlying browser/wallet promise ignores it. */
export function abortable<T>(operation: () => Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(signal.reason); return; }
    const cleanup = () => signal.removeEventListener("abort", onAbort);
    const onAbort = () => { cleanup(); reject(signal.reason); };
    signal.addEventListener("abort", onAbort, { once: true });
    try {
      operation().then((value) => { cleanup(); resolve(value); }, (error: unknown) => { cleanup(); reject(error); });
    } catch (error) { cleanup(); reject(error); }
  });
}

/** Chunk long Retry-After values instead of overflowing the browser timer limit. */
export async function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  let remaining = Math.max(0, milliseconds);
  while (remaining > 0) {
    signal?.throwIfAborted();
    const duration = Math.min(remaining, 2_147_483_647);
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => signal?.removeEventListener("abort", onAbort);
      const onAbort = () => { clearTimeout(timer); cleanup(); reject(signal?.reason); };
      const timer = setTimeout(() => { cleanup(); resolve(); }, duration);
      signal?.addEventListener("abort", onAbort, { once: true });
    });
    remaining -= duration;
  }
  signal?.throwIfAborted();
}
